import { useIngressesAndUploads } from "./lib/useIngressesAndUploads";
import { UploadStatus } from "lib/types/UploadStatus";
import UploadSvg from "components/icons/UploadIcons/upload.svg";
import UploadErrorSvg from "components/icons/UploadIcons/upload-error.svg";
import { FilterOption } from "./IngressJobsModal";
import { IngressListItemModel } from "./lib/IngressListItemModel";

interface IngressOverviewProps {
  readonly filter?: FilterOption;
}

function filterIngressesByStatus(
  ingresses: IngressListItemModel[],
  filter: FilterOption,
): IngressListItemModel[] {
  if (filter === "all") {
    return ingresses;
  }

  return ingresses.filter((ingress) => {
    const isInProgress =
      ingress.uploadStatus === UploadStatus.uploading ||
      ingress.uploadStatus === UploadStatus.preparing ||
      ingress.uploadStatus === UploadStatus.queued ||
      ingress.ingressStatus === "uploading" ||
      ingress.ingressStatus === "processing" ||
      ingress.ingressStatus === "awaitingProcessing";

    const isComplete =
      (ingress.ingressStatus === "done" ||
        ingress.ingressStatus === "error" ||
        ingress.ingressStatus === "partialError" ||
        ingress.uploadStatus === UploadStatus.error) &&
      ingress.ingressStatus !== "awaitingProcessing" &&
      ingress.uploadStatus !== UploadStatus.preparing;

    if (filter === "in-progress") {
      return isInProgress;
    }

    if (filter === "complete") {
      return isComplete;
    }

    return false;
  });
}

function SummaryCard({
  count,
  title,
  color,
  Icon,
}: {
  count: number;
  title: string;
  color: string;
  Icon: React.ElementType;
}) {
  return (
    <div className="overflow-clip rounded-lg border border-neutral-200 p-4">
      <Icon className={`mb-2 h-6 w-6 ${color}`} />
      <div className="px-1 text-lg">{count}</div>
      <div className="px-1 text-sm">{title}</div>
    </div>
  );
}

export default function IngressOverview({
  filter = "all",
}: IngressOverviewProps) {
  const { ingressesAndUploads } = useIngressesAndUploads();

  // Filter ingresses based on selected filter
  const filteredIngresses = ingressesAndUploads
    ? filterIngressesByStatus(ingressesAndUploads, filter)
    : [];

  // Count ingresses by status from filtered data
  const importingCount = filteredIngresses.filter(
    (item) =>
      item.uploadStatus === UploadStatus.uploading ||
      item.ingressStatus === "uploading" ||
      item.uploadStatus === UploadStatus.queued,
  ).length;

  const processingCount = filteredIngresses.filter(
    (item) =>
      item.ingressStatus === "processing" ||
      item.ingressStatus === "awaitingProcessing",
  ).length;

  const unsuccessfulCount = filteredIngresses.filter(
    (item) =>
      item.ingressStatus === "error" ||
      item.ingressStatus === "partialError" ||
      item.uploadStatus === UploadStatus.error,
  ).length;

  const successfulCount = filteredIngresses.filter(
    (item) =>
      item.ingressStatus === "done" &&
      (item.uploadStatus === UploadStatus.complete ||
        item.uploadStatus === null),
  ).length;

  // Check if any uploads are active
  const hasActiveUploads = filteredIngresses.some(
    (item) =>
      item.uploadStatus === UploadStatus.uploading ||
      item.uploadStatus === UploadStatus.preparing ||
      item.uploadStatus === UploadStatus.queued ||
      item.ingressStatus === "uploading",
  );

  return (
    <div className="flex h-full flex-col">
      <h2 className="text-md rounded-lg rounded-b-none border border-b-0 border-neutral-200 p-4">
        Overview
      </h2>

      <div className="flex-1 overflow-auto rounded-lg rounded-t-none border border-t-0 border-neutral-200 p-4 pt-0">
        <div className="grid grid-cols-2 gap-4">
          {filter !== "complete" && (
            <SummaryCard
              count={importingCount}
              title="Importing"
              color="text-sky-500"
              Icon={UploadSvg}
            />
          )}

          {filter !== "complete" && (
            <SummaryCard
              count={processingCount}
              title="Processing"
              color="text-warning-500"
              Icon={UploadSvg}
            />
          )}

          {unsuccessfulCount > 0 && (
            <SummaryCard
              count={unsuccessfulCount}
              title="Unsuccessful"
              color="text-red-500"
              Icon={UploadErrorSvg}
            />
          )}

          {filter !== "in-progress" && (
            <SummaryCard
              count={successfulCount}
              title="Successful"
              color="text-green-500"
              Icon={UploadSvg}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-md mt-4 flex items-center gap-x-4 rounded-lg border p-4 text-neutral-600">
        <div className={hasActiveUploads ? "text-red-500" : "text-green-500"}>
          &bull;
        </div>
        <div>
          {hasActiveUploads
            ? "If you exit Keyframe now, your uploads will be cancelled"
            : "It's safe to exit Keyframe. Processing files will continue"}
        </div>
      </div>
    </div>
  );
}
